import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@lightspeed/flame/Text';

import { useMoney } from '~/renderer/hooks/.';
import { applyDecimalFormat } from '~/renderer/utils/currency';

import { ReceiptCalcRow, ReceiptSummaryWrapper, ReceiptTotalRow } from './Receipt.style';

import type { Sale } from '~/renderer/store/receipt/types';

interface ReceiptSummaryProps {
  hasUntaxedItems: boolean;
  sale: Sale | null;
}

interface NoTaxProps {
  hasUntaxedItems: boolean;
}

interface SubTotalProps {
  subtotal: number | undefined;
}

interface TaxProps {
  taxName: string | undefined;
  taxRate: string | undefined;
  taxAmount: string;
  taxNumber: number;
}

interface TotalProps {
  total: number | undefined;
}

interface TipProps {
  tips: number | undefined | null;
}

const NoTax: React.FunctionComponent<NoTaxProps> = ({ hasUntaxedItems }) => {
  const { t } = useTranslation();

  if (!hasUntaxedItems) return null;
  return (
    <ReceiptCalcRow>
      <Text lineHeight="normal" size="small" textTransform="uppercase" data-testid="sale-no-tax">
        * {t('receipt.noTaxApplied')}
      </Text>
    </ReceiptCalcRow>
  );
};

const SubTotal: React.FunctionComponent<SubTotalProps> = ({ subtotal }) => {
  const { t } = useTranslation();
  const { money } = useMoney();

  if (!subtotal) return null;
  return (
    <ReceiptCalcRow>
      <Text lineHeight="normal" data-testid="sale-subtotal-title">
        {t('receipt.subtotal')}
      </Text>
      <Text lineHeight="normal" size="small" data-testid="sale-subtotal">
        {money(subtotal)}
      </Text>
    </ReceiptCalcRow>
  );
};

const Tax: React.FunctionComponent<TaxProps> = ({ taxName, taxRate, taxAmount, taxNumber }) => {
  const { t } = useTranslation();
  if (!taxName) return null;

  return (
    <ReceiptCalcRow>
      <Text
        lineHeight="normal"
        size="small"
        textTransform="uppercase"
        data-testid={`sale-tax${taxNumber}-name`}
      >
        {taxName}
        {taxRate ? (
          ` (${taxRate}%)`
        ) : (
          <Text lineHeight="normal" size="small" textTransform="none" as="span">
            {' '}
            {t('receipt.total')}
          </Text>
        )}
      </Text>
      <Text lineHeight="normal" size="small" data-testid={`sale-tax${taxNumber}-amount`}>
        {taxAmount}
      </Text>
    </ReceiptCalcRow>
  );
};

const Total: React.FunctionComponent<TotalProps> = ({ total }) => {
  const { t } = useTranslation();
  const { money } = useMoney();

  return (
    <ReceiptTotalRow>
      <Text data-testid="sale-total-title" lineHeight="normal" style={{ fontSize: '1.75rem' }}>
        {t('receipt.total')}:
      </Text>
      <Text lineHeight="normal" style={{ fontSize: '1.75rem' }} data-testid="sale-total">
        {money(total)}
      </Text>
    </ReceiptTotalRow>
  );
};

const Tip: React.FC<TipProps> = ({ tips = null }) => {
  const { t } = useTranslation();
  const { money } = useMoney();

  if (typeof tips === 'number') {
    return (
      <ReceiptCalcRow>
        <Text lineHeight="normal" data-testid="sale-tip-title">
          {t('receipt.tip')}
        </Text>
        <Text lineHeight="normal" size="small" data-testid="sale-tip">
          {money(tips)}
        </Text>
      </ReceiptCalcRow>
    );
  }
  return null;
};

const ReceiptSummary: React.FunctionComponent<ReceiptSummaryProps> = ({
  hasUntaxedItems,
  sale,
}) => {
  const { money } = useMoney();

  const {
    calcTotal: total,
    calcSubtotal: subtotal,
    TaxClassTotals,
    calcTips: tips,
    calcTax1,
    calcTax2,
  } = sale || {};
  const firstTaxName = TaxClassTotals?.Tax[0]?.taxname;
  const rawFirstTaxRate = TaxClassTotals?.Tax[0]?.rate;
  // @NOTE In case there are multiple tax classes and they're not of the same rate, do not display the % near the tax name
  const isSameFirstTaxRate = rawFirstTaxRate
    ? !TaxClassTotals.Tax.some(({ rate }) => rate !== rawFirstTaxRate)
    : false;
  const firstTaxRate =
    rawFirstTaxRate && isSameFirstTaxRate ? applyDecimalFormat(rawFirstTaxRate) : undefined;
  const firstTaxAmount = money(calcTax1);
  const secondTaxName = TaxClassTotals?.Tax[0]?.taxname2;
  const rawSecondTaxRate = TaxClassTotals?.Tax[0]?.rate2;
  const isSameSecondTaxRate = rawSecondTaxRate
    ? !TaxClassTotals.Tax.some(({ rate2 }) => rate2 !== rawSecondTaxRate)
    : false;
  const secondTaxRate =
    rawSecondTaxRate && isSameSecondTaxRate ? applyDecimalFormat(rawSecondTaxRate) : undefined;
  const secondTaxAmount = money(calcTax2);

  return (
    <ReceiptSummaryWrapper>
      <NoTax hasUntaxedItems={hasUntaxedItems} />
      <Tax taxAmount={firstTaxAmount} taxName={firstTaxName} taxRate={firstTaxRate} taxNumber={1} />
      <Tax
        taxAmount={secondTaxAmount}
        taxName={secondTaxName}
        taxRate={secondTaxRate}
        taxNumber={2}
      />
      <SubTotal subtotal={subtotal} />
      <Tip tips={tips} />
      <Total total={total} />
    </ReceiptSummaryWrapper>
  );
};

export default ReceiptSummary;
